import {
  type DocumentSummary,
  formatBytes,
  INGESTION_STATES,
  type ListDocumentsQuery,
} from '@hvac/contracts';
import { Alert, cn, Spinner } from '@hvac/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requestExtraction } from '../../shared/api/extraction-queries.js';
import { Link } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';
import { documentListQuery } from '../../shared/api/document-queries.js';
import { DocumentThumbnail } from './document-thumbnail.js';

/**
 * The document list.
 *
 * Built for millions of rows: keyset cursors from the API, an infinite query
 * on top, and row virtualisation so the DOM holds a screenful rather than the
 * whole result. Scrolling past ten thousand receipts costs the same as
 * scrolling past ten.
 */

const STATE_LABELS: Record<string, string> = {
  pending_upload: 'Awaiting upload',
  uploaded: 'Uploaded',
  validated: 'Validated',
  stored: 'Stored',
  queued: 'Queued',
  processing: 'Processing',
  completed: 'Ready',
  retryable: 'Will retry',
  failed: 'Failed',
  archived: 'Archived',
};

/**
 * A failure a person can act on. The provider's raw JSON is accurate and
 * useless: what matters is whether the document is at fault, and what to do.
 */
function humaniseFailure(reason: string): string {
  if (/overloaded|rate.?limit|529|503/i.test(reason)) {
    return 'The reader was busy when this arrived. Nothing is wrong with the file — press Try again.';
  }
  if (/timeout|timed out/i.test(reason)) {
    return 'Reading this took too long. Try again, and if it repeats the file may be very large.';
  }
  if (/refus|policy/i.test(reason)) {
    return 'The reader declined this document. A person can still record it by hand.';
  }
  if (/unreadable|corrupt|could not read/i.test(reason)) {
    return 'These bytes could not be read as a document. It may be corrupt, or not what its name says.';
  }
  return reason;
}

const STATE_TONE: Record<string, string> = {
  completed: 'bg-success-surface text-success',
  failed: 'bg-danger-surface text-danger',
  retryable: 'bg-warning-surface text-warning',
  archived: 'bg-surface-sunken text-content-subtle',
};

export interface DocumentListProps {
  readonly organizationId: string;
}

export function DocumentList({ organizationId }: DocumentListProps) {
  const queryClient = useQueryClient();
  // A failed read is usually the provider having a bad minute, so the cure is
  // one button rather than a re-upload the duplicate check would refuse.
  const retry = useMutation({
    mutationFn: (documentId: string) => requestExtraction(organizationId, documentId, true),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['intake', organizationId] });
    },
  });
  const [filters, setFilters] = useState<Partial<ListDocumentsQuery>>({ limit: 50 });
  const [searchInput, setSearchInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Debounced so typing a filename does not fire a request per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => {
        const trimmed = searchInput.trim();
        const { query: _removed, ...rest } = current;
        return trimmed === '' ? rest : { ...rest, query: trimmed };
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const query = useInfiniteQuery(documentListQuery(organizationId, filters));
  const documents = query.data?.pages.flatMap((page) => page.items) ?? [];

  // React's compiler lint warns that TanStack Virtual cannot be memoised: it
  // measures the DOM during render by design. The warning is accurate and
  // accepted, the alternative is rendering every row, which defeats the point.
  const virtualizer = useVirtualizer({
    count: documents.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 76,
    // Render a little beyond the viewport so fast scrolling does not show gaps.
    overscan: 8,
  });

  // Fetch the next page when the last rendered row approaches the end.
  const virtualRows = virtualizer.getVirtualItems();
  const lastRow = virtualRows.at(-1);
  useEffect(() => {
    if (lastRow === undefined) return;
    if (lastRow.index >= documents.length - 10 && query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
  }, [lastRow, documents.length, query]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search by file name"
          aria-label="Search documents by file name"
          className="min-w-48 flex-1 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
        />

        <select
          aria-label="Filter by status"
          value={filters.state ?? ''}
          onChange={(event) =>
            setFilters((current) => {
              const { state: _removed, ...rest } = current;
              return event.target.value === ''
                ? rest
                : { ...rest, state: event.target.value as ListDocumentsQuery['state'] };
            })
          }
          className="rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
        >
          <option value="">Any status</option>
          {INGESTION_STATES.map((state) => (
            <option key={state} value={state}>
              {STATE_LABELS[state] ?? state}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-content-muted">
          <input
            type="checkbox"
            checked={filters.includeArchived ?? false}
            onChange={(event) =>
              setFilters((current) => ({ ...current, includeArchived: event.target.checked }))
            }
          />
          Include archived
        </label>
      </div>

      {query.isError ? (
        <Alert tone="danger" title="Could not load documents">
          {query.error instanceof Error ? query.error.message : 'Please try again.'}
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner className="size-6 text-brand-600" label="Loading documents" />
        </div>
      ) : documents.length === 0 ? (
        <p className="py-12 text-center text-sm text-content-muted">
          {filters.query === undefined && filters.state === undefined
            ? 'No documents yet. Drop some receipts above to get started.'
            : 'No documents match those filters.'}
        </p>
      ) : (
        <div
          ref={scrollRef}
          className="max-h-[32rem] overflow-y-auto rounded-[var(--radius-surface)] border border-border bg-surface"
        >
          {/* The spacer carries the full scroll height; only the visible rows
              exist in the DOM, absolutely positioned into place. */}
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualRows.map((virtualRow) => {
              const document = documents[virtualRow.index];
              if (document === undefined) return null;
              return (
                <div
                  key={document.id}
                  ref={virtualizer.measureElement}
                  data-index={virtualRow.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <DocumentRow
                    organizationId={organizationId}
                    document={document}
                    onRetry={(id) => retry.mutate(id)}
                    retryingId={retry.isPending ? retry.variables : null}
                  />
                </div>
              );
            })}
          </div>

          {query.isFetchingNextPage ? (
            <div className="flex justify-center py-4">
              <Spinner className="size-5 text-brand-600" label="Loading more" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function DocumentRow({
  organizationId,
  document,
  onRetry,
  retryingId,
}: {
  onRetry: (documentId: string) => void;
  retryingId: string | null;
  organizationId: string;
  document: DocumentSummary;
}) {
  const receivedOn = new Date(document.receivedAt).toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Link
      to="/documents/$documentId"
      params={{ documentId: document.id }}
      className="flex items-center gap-3 border-b border-border px-3 py-3 hover:bg-surface-sunken"
    >
      <DocumentThumbnail
        organizationId={organizationId}
        documentId={document.id}
        hasPreview={document.hasPreview}
        mimeType={document.mimeType}
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-content">
          {document.filename ?? 'Untitled document'}
        </p>
        <p className="truncate text-xs text-content-muted">
          {receivedOn}
          {document.uploader.fullName !== null ? ` · ${document.uploader.fullName}` : ''}
          {document.byteSize !== null ? ` · ${formatBytes(document.byteSize)}` : ''}
          {document.versionCount > 1 ? ` · ${document.versionCount} versions` : ''}
        </p>
        {document.failureReason !== null ? (
          <p className="text-xs text-danger">{humaniseFailure(document.failureReason)}</p>
        ) : null}
      </div>

      {document.ingestionState === 'failed' ? (
        <button
          type="button"
          onClick={(event) => {
            // The row is a link; retrying must not navigate away from it.
            event.preventDefault();
            event.stopPropagation();
            onRetry(document.id);
          }}
          disabled={retryingId === document.id}
          className={cn(
            'shrink-0 rounded-[var(--radius-control)] border border-border px-3 py-1.5 text-xs font-medium',
            'transition-all duration-[var(--duration-quick)]',
            'hover:border-brand-300 hover:text-brand-700 hover:shadow-[var(--shadow-glow)]',
            'disabled:opacity-60',
          )}
        >
          {retryingId === document.id ? 'Trying…' : 'Try again'}
        </button>
      ) : null}

      <span
        className={cn(
          'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
          STATE_TONE[document.ingestionState] ?? 'bg-surface-sunken text-content-muted',
        )}
      >
        {STATE_LABELS[document.ingestionState] ?? document.ingestionState}
      </span>
    </Link>
  );
}
